import { Currency, type Price } from '@backmarket/http-api'

/**
 * Parses a formatted price string (e.g., "$211.00", "€42,00", "A$150.00")
 * and converts it back to a Price object with amount and currency.
 *
 * @param formattedPrice - A locale-formatted price string with currency symbol
 * @returns Price object with {amount, currency} or null if parsing fails
 *
 * @example
 * parseFormattedPrice("$211.00") // { amount: "211.00", currency: "USD" }
 * parseFormattedPrice("€42,00") // { amount: "42.00", currency: "EUR" }
 * parseFormattedPrice("A$150.00") // { amount: "150.00", currency: "AUD" }
 */
export function parseFormattedPrice(formattedPrice: string): Price | null {
  if (!formattedPrice || typeof formattedPrice !== 'string') {
    return null
  }

  // Mapping of currency symbols to Currency enum
  const currencyMap: Record<string, Currency> = {
    '€': Currency.EUR,
    $: Currency.USD,
    '£': Currency.GBP,
    '¥': Currency.JPY,
    kr: Currency.SEK,
  }

  // Detect currency
  let currency: Currency | null = null
  let priceString = formattedPrice

  // Check for AUD (A$ prefix)
  if (priceString.includes('A$')) {
    currency = Currency.AUD
    priceString = priceString.replace('A$', '')
  } else {
    // Check for other currency symbols
    for (const [symbol, curr] of Object.entries(currencyMap)) {
      if (priceString.includes(symbol)) {
        currency = curr
        priceString = priceString.replace(symbol, '')
        break
      }
    }
  }

  if (!currency) {
    return null
  }

  // Remove all whitespace
  let amount = priceString.trim()

  // Determine decimal separator: it is followed by exactly 2 digits.
  // A separator followed by 3 digits is a thousands separator.
  const lastDot = amount.lastIndexOf('.')
  const lastComma = amount.lastIndexOf(',')
  const lastSeparator = Math.max(lastDot, lastComma)

  if (lastSeparator !== -1) {
    const digitsAfterLastSeparator = amount
      .slice(lastSeparator + 1)
      .replace(/[^\d]/g, '').length
    const lastSeparatorIsDecimal = digitsAfterLastSeparator === 2

    if (lastSeparatorIsDecimal) {
      if (lastComma > lastDot) {
        // Comma is decimal, dot is thousands separator
        amount = amount.replace(/\./g, '').replace(',', '.')
      } else {
        // Dot is decimal, comma is thousands separator
        amount = amount.replace(/,/g, '')
      }
    } else {
      // Separator is a thousands separator (e.g. ¥123,456 or €1.234)
      amount = amount.replace(/[.,]/g, '')
    }
  }

  // Remove any remaining non-digit characters except dot
  amount = amount.replace(/[^\d.]/g, '')

  // Validate that we have a valid number
  if (!amount || Number.isNaN(parseFloat(amount))) {
    return null
  }

  return {
    amount,
    currency,
  }
}
